import { SgActivityMetrics } from '@client/components/_business/sendgrid/SgActivityMetrics';
import { SgMessageDetailsDrawer } from '@client/components/_business/sendgrid/SgMessageDetailsDrawer';
import { SgMessagesTable } from '@client/components/_business/sendgrid/SgMessagesTable';
import {
    type EmailActivityQuickFilterType,
    SgQuickFilters,
} from '@client/components/_business/sendgrid/SgQuickFilters';
import { PageController } from '@client/components/_core/layout/PageController';
import type { RouterInputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Collapse } from 'antd';
import { BarChart3 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';

export const Route = createFileRoute('/admin-console/monitoring/email-activity/')({
    staticData: {
        breadcrumb: { title: 'Email Activity' },
        ui: {
            title: 'Email Activity',
            subtitle: 'Track and monitor email delivery, opens, clicks and other engagement metrics',
            widthVariant: 'fullWidth',
            menuDescription: 'Email delivery tracking',
        },
    },
    validateSearch: zodValidator(
        z.object({
            email: z.string().optional(),
        }),
    ),
    component: EmailActivity,
});

type Where = RouterInputs['admin']['sendgrid']['listSgMessages']['where'];

function EmailActivity() {
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
    const [quickFilter, setQuickFilter] = useState<EmailActivityQuickFilterType>('all');
    const searchParams = Route.useSearch();

    // Date range for metrics display (last 7 days by default)
    const [metricsDateRange] = useState<[string, string]>([
        DateTime.now().minus({ days: 7 }).startOf('day').toISO(),
        DateTime.now().endOf('day').toISO(),
    ]);

    // Apply quick filters to generate where clause
    const quickFilterWhere = useMemo<Where>(() => {
        return match(quickFilter)
            .with('all', () => ({}) satisfies Where)
            .with('delivered', () => ({ event: 'delivered' }) satisfies Where)
            .with('opened', () => ({ event: 'open' }) satisfies Where)
            .with('clicked', () => ({ event: 'click' }) satisfies Where)
            .with('processing', () => ({ event: 'processed' }) satisfies Where)
            .with('deferred', () => ({ event: 'deferred' }) satisfies Where)
            .with('failed', () => ({ event: { in: ['bounce', 'dropped'] } }) satisfies Where)
            .with('blocked', () => ({ event: { in: ['blocked', 'spamreport'] } }) satisfies Where)
            .with(
                'unsubscribed',
                () => ({ event: { in: ['unsubscribe', 'group_unsubscribe', 'group_resubscribe'] } }) satisfies Where,
            )
            .exhaustive();
    }, [quickFilter]);

    const handleSelectMessage = (sgMessageId: string) => {
        setSelectedMessageId(sgMessageId);
    };

    const handleCloseDetails = () => {
        setSelectedMessageId(null);
    };

    return (
        <PageController route={Route}>
            <div className="space-y-4">
                {/* Metrics Panel */}
                <Collapse
                    items={[
                        {
                            key: 'metrics',
                            label: (
                                <div className="flex items-center gap-2">
                                    <BarChart3 className="h-4 w-4" />
                                    <span className="font-medium">Email Statistics (Last 7 Days)</span>
                                </div>
                            ),
                            children: (
                                <SgActivityMetrics startDate={metricsDateRange[0]} endDate={metricsDateRange[1]} />
                            ),
                        },
                    ]}
                />

                {/* Quick Filters */}
                <SgQuickFilters quickFilter={quickFilter} setQuickFilter={setQuickFilter} />

                {/* Messages Table */}
                <SgMessagesTable
                    onSelectMessage={handleSelectMessage}
                    initialEmail={searchParams.email}
                    quickFilterWhere={quickFilterWhere}
                />
            </div>

            {/* Message Details Drawer */}
            <SgMessageDetailsDrawer sgMessageId={selectedMessageId} onClose={handleCloseDetails} />
        </PageController>
    );
}
